/**
 * The server's draft evaluation, in the Live Impact panel's vocabulary
 * (SE-8458).
 *
 * The action modals used to compute their verdict on the client from two
 * hardcoded constants in `adapters/complianceFixtures.ts` — a club with €220M
 * revenue, a €110M wage bill and a squad already at the registration limit —
 * so every organization was measured against the same invented club. The
 * backend evaluates unsaved scenario actions against the club's REAL rules,
 * figures and squad (SE-7410), and this module is the translation between what
 * it returns and what the panel renders.
 *
 * It is pure and takes STRUCTURAL inputs rather than a generated query type, so
 * it is exercisable without Apollo, without a mock link and without the
 * network. The thin hook that actually runs the query lives in
 * `adapters/draftEvaluationSource.ts`; everything decidable lives here.
 * @module features/financial-compliance/domain/draftEvaluation
 */

import { ruleSummaryFor } from "./ruleSummary";
import { isNotVerifiable } from "./squadCostEvaluation";

import {
  ComplianceQuantity,
  ComplianceRuleLayer,
  ComplianceRuleState,
  type CompliancePartialReason,
  type ComplianceQuantityUnit,
} from "@/generated/graphql";

import type { ComplianceRuleEvaluation, RuleState } from "../types";

/** One server-evaluated rule, reduced to the fields this module reads. */
export interface DraftRuleEvaluation {
  /** Stable rule identity. */
  readonly ruleId: string;
  /** The rule's registered name. */
  readonly ruleName: string;
  /** The verdict the server reached. */
  readonly state: ComplianceRuleState;
  /** The measured value, or null when unmeasurable. */
  readonly value?: number | null;
  /** The limit, or null when unconfigured. */
  readonly threshold?: number | null;
  /** What both figures are denominated in. */
  readonly unit: ComplianceQuantityUnit;
  /** Why a PARTIAL rule withheld a verdict (SE-8798). */
  readonly partialReasons: readonly CompliancePartialReason[];
  /** The figures a not-verifiable rule needs, as the backend names them (SE-8798). */
  readonly unverifiableFigures: readonly string[];
}

/** One layer group as the server returns it. */
export interface DraftLayerGroup {
  /** Which regulatory layer. */
  readonly layer: ComplianceRuleLayer;
  /** Its rules, in the server's registry order. */
  readonly evaluations: readonly DraftRuleEvaluation[];
}

/**
 * Server state → the panel's own rule vocabulary.
 *
 * `PARTIAL` becomes `awaiting-data` rather than anything judgeable. That is the
 * SE-8378 contract: a rule the server could not fully compute is a LOWER BOUND,
 * and a lower bound is never reported as compliance.
 */
const RULE_STATE: Readonly<Record<ComplianceRuleState, RuleState>> = {
  [ComplianceRuleState.Red]: "breach",
  [ComplianceRuleState.Amber]: "at-risk",
  [ComplianceRuleState.Green]: "on-track",
  [ComplianceRuleState.Partial]: "awaiting-data",
  [ComplianceRuleState.NotConfigured]: "not-configured",
};

/**
 * A rule's panel state. A rule the app cannot verify (SE-8798) is
 * "not-configured" — not judgeable — and NEVER "awaiting-data": that would
 * make the whole verdict INCOMPLETE ("at least N alerts"), telling the reader
 * a figure is missing that the club has no field to enter.
 * @param evaluation - The server's rule.
 * @returns The panel's state for it.
 */
const panelStateFor = (evaluation: DraftRuleEvaluation): RuleState =>
  isNotVerifiable(evaluation) ? "not-configured" : RULE_STATE[evaluation.state];

/**
 * Compact layer labels for the modal's headline rows.
 *
 * Deliberately NOT the Compliance tab's own column titles ("Objectives — board
 * covenants", …): those are sized to head a column on a full-width board, and
 * this label is non-visible metadata on a one-line row inside a modal. The
 * VISIBLE sentence is the rule summary, which both surfaces share through
 * {@link ruleSummaryFor} — that is the wording SE-8458 requires to agree, and
 * it does.
 */
const LAYER_LABEL: Readonly<Record<ComplianceRuleLayer, string>> = {
  [ComplianceRuleLayer.Internal]: "Objectives",
  [ComplianceRuleLayer.Continental]: "Continental",
  [ComplianceRuleLayer.Domestic]: "Domestic",
};

/**
 * Translate the server's layer groups into the panel's rule list.
 *
 * Order is preserved exactly — the server's layer order, and within a layer its
 * registry order — so the panel does not reshuffle its rows between keystrokes.
 * `headlinesByLayer` then reduces each layer to its worst rule.
 * @param layers - The server's layer groups.
 * @returns Every rule, in render order.
 */
export const toPanelRules = (
  layers: readonly DraftLayerGroup[]
): readonly ComplianceRuleEvaluation[] =>
  layers.flatMap(group =>
    group.evaluations.map(evaluation => ({
      ruleId: evaluation.ruleId,
      layerId: group.layer,
      layerLabel: LAYER_LABEL[group.layer],
      state: panelStateFor(evaluation),
      headline: ruleSummaryFor(evaluation),
    }))
  );

/**
 * The club's net book value before this scenario, or null when it is unknown.
 *
 * SE-8794. The server's `NET_BOOK_VALUE` is SUBJECT-scoped — one figure per
 * player — so it is never among the CLUB-scoped `clubQuantities` this used to
 * read, and the row said "Not configured" for every club, for a figure no club
 * can configure. The club's book is the sum of its roster's, which the caller
 * computes (`useLiveImpact`, First Team + Academy — never watchlist targets,
 * who are not on the books until signed). What is decided here is whether the
 * viewer may see it: a quantity the tier's SE-7013 allow-list withholds is not
 * re-derived from per-player rows the client happens to hold.
 *
 * Null is not zero and must not be rendered as one — the panel prints a dash.
 * @param rosterNetBookValue - The roster's summed book value, or null when no
 * player carries one.
 * @param permittedQuantities - The quantities the viewer's tier may see.
 * @returns The club's book value, or null when unknown or withheld.
 */
export const clubNetBookValueFrom = (
  rosterNetBookValue: number | null,
  permittedQuantities: readonly ComplianceQuantity[]
): number | null =>
  permittedQuantities.includes(ComplianceQuantity.NetBookValue)
    ? rosterNetBookValue
    : null;
